import React, { useState, useCallback } from "react";
import moment from "moment";

import RefundDetails from "./RefundDetails";
import ConfirmationModal from "./ConfirmationModal";
import { formatNumber } from "../../utils/convertToNumber";

const ChevronDownIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const StatusBadge = ({ status }) => {
  const statusStyles = {
    confirmed: "bg-yellow-100 text-yellow-800",
    initiated: "bg-blue-100 text-blue-800",
    completed: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-800",
  };

  return (
    <span
      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
        statusStyles[status] || "bg-gray-100 text-gray-800"
      }`}
    >
      {status}
    </span>
  );
};

const ActionButton = ({ onClick, disabled, variant = "primary", children }) => {
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-700",
    success: "bg-green-600 hover:bg-green-700",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-1 text-xs font-medium text-white rounded-md shadow-sm disabled:opacity-50 ${variants[variant]}`}
    >
      {children}
    </button>
  );
};

const RefundRow = React.memo(
  ({
    booking,
    onStatusUpdate,
    onRefundClick,
    isExpanded,
    onToggleExpand,
    index,
  }) => {
    const [isUpdating, setIsUpdating] = useState(false);
    const [modals, setModals] = useState({
      initiate: false,
      refund: false,
    });

    const openModal = useCallback((type) => {
      setModals((prev) => ({ ...prev, [type]: true }));
    }, []);

    const closeModal = useCallback((type) => {
      setModals((prev) => ({ ...prev, [type]: false }));
    }, []);

    const handleInitiateClick = useCallback(() => {
      openModal("initiate");
    }, [openModal]);

    const handleRefundClick = useCallback(() => {
      openModal("refund");
    }, [openModal]);

    const handleConfirmUpdate = useCallback(async () => {
      closeModal("initiate");
      setIsUpdating(true);
      try {
        await onStatusUpdate(booking._id, "initiated");
      } finally {
        setIsUpdating(false);
      }
    }, [booking._id, onStatusUpdate, closeModal]);

    const handleConfirmRefund = useCallback(async () => {
      closeModal("refund");
      setIsUpdating(true);
      try {
        await onRefundClick(booking._id);
      } finally {
        setIsUpdating(false);
      }
    }, [booking._id, onRefundClick, closeModal]);

    const stopPropagation = useCallback((e) => {
      e.stopPropagation();
    }, []);

    return (
      <>
        <tr
          className="transition-colors cursor-pointer hover:bg-gray-50"
          onClick={onToggleExpand}
        >
          <td className="px-4 py-3 whitespace-nowrap">
            <div className="font-mono text-sm font-semibold text-gray-900">
              {index + 1}
            </div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <div className="font-mono text-sm font-semibold text-gray-900">
              {booking.flight.PNR}
            </div>
            <div className="text-xs text-gray-500">{booking.company.name}</div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <div className="text-sm text-gray-900">
              {booking.flight.origin} → {booking.flight.destination}
            </div>
            <div className="text-xs text-gray-500">
              {moment(booking.flight.departureTime).format("DD/MM/YYYY")}
            </div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <div className="text-sm font-semibold text-green-600">
              {formatNumber(booking.toBeRefunded)} {booking.currency}
            </div>
            <div className="text-xs text-gray-500">
              Total: {formatNumber(booking.totalPrice)} {booking.currency}
            </div>
          </td>
          <td className="px-4 py-3 capitalize whitespace-nowrap">
            <StatusBadge status={booking.status} />
          </td>
          <td className="px-4 py-3 text-sm font-medium text-right whitespace-nowrap">
            <div className="flex justify-end items-center space-x-2">
              {booking.status === "confirmed" && (
                <ActionButton
                  onClick={(e) => {
                    stopPropagation(e);
                    handleInitiateClick();
                  }}
                  disabled={isUpdating}
                  variant="primary"
                >
                  {isUpdating ? "Processing..." : "Initiate"}
                </ActionButton>
              )}
              {booking.status === "initiated" && (
                <ActionButton
                  onClick={(e) => {
                    stopPropagation(e);
                    handleRefundClick();
                  }}
                  disabled={isUpdating}
                  variant="success"
                >
                  {isUpdating ? "Processing..." : "Refund"}
                </ActionButton>
              )}
              <button
                className="p-1 text-gray-400 rounded-full transition-colors hover:bg-gray-200"
                onClick={(e) => {
                  stopPropagation(e);
                  onToggleExpand();
                }}
              >
                <ChevronDownIcon
                  className={`w-5 h-5 transition-transform transform ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>
          </td>
        </tr>
        {isExpanded && <RefundDetails booking={booking} />}

        <ConfirmationModal
          isOpen={modals.initiate}
          onClose={() => closeModal("initiate")}
          onConfirm={handleConfirmUpdate}
          title="Initiate Refund"
        >
          <p>
            Are you sure you want to mark the booking for PNR{" "}
            <span className="font-mono font-bold">{booking.flight.PNR}</span> as
            initiated? This action cannot be undone.
          </p>
        </ConfirmationModal>

        <ConfirmationModal
          isOpen={modals.refund}
          onClose={() => closeModal("refund")}
          onConfirm={handleConfirmRefund}
          title="Refund Request"
        >
          <p>
            Are you sure you want to refund booking for PNR{" "}
            <span className="font-mono font-bold">{booking.flight.PNR}</span>?
            This action cannot be undone.
          </p>
        </ConfirmationModal>
      </>
    );
  }
);

const TABLE_HEADERS = [
  { key: "#", label: "#" },
  { key: "pnr", label: "PNR / Carrier" },
  { key: "route", label: "Route / Date" },
  { key: "refund", label: "Refund / Total" },
  { key: "status", label: "Status" },
  { key: "actions", label: "Actions", sr: true },
];

const RefundsTable = ({ refunds, onStatusUpdate, onRefundClick }) => {
  const [expandedBooking, setExpandedBooking] = useState(null);

  const toggleExpand = useCallback((bookingId) => {
    setExpandedBooking((prev) => (prev === bookingId ? null : bookingId));
  }, []);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {TABLE_HEADERS.map((header) => (
              <th
                key={header.key}
                scope="col"
                className={`px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase ${
                  header.sr ? "relative" : ""
                }`}
              >
                {header.sr ? (
                  <span className="sr-only">{header.label}</span>
                ) : (
                  header.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {refunds.map((booking, index) => (
            <RefundRow
              key={booking._id}
              booking={booking}
              onStatusUpdate={onStatusUpdate}
              onRefundClick={onRefundClick}
              isExpanded={expandedBooking === booking._id}
              onToggleExpand={() => toggleExpand(booking._id)}
              index={index}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

RefundRow.displayName = "RefundRow";

export default RefundsTable;
